import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import { useNavigate, useLocation } from 'react-router-dom';
import logo from '../../assets/cricket-player.png';
import './index.css';

const pages = [
  { name: 'Live Scores', navigate: '/match' },
  { name: 'Blogs', navigate: '/blogs' },
  { name: 'Stats', navigate: '/stats' },
  { name: 'About Us', navigate: '/about-us' }
];

const settings = [
  { name: 'Profile', navigate: '/profile' },
  { name: 'Ledger', navigate: '/ledger' },
  { name: 'Logout', navigate: 'logout' }
];

const menuPaperSx = {
  '& .MuiPaper-root': {
    background: 'linear-gradient(160deg, rgba(8, 29, 39, 0.96), rgba(19, 56, 62, 0.94))',
    color: '#edf7f4',
    border: '1px solid rgba(255, 255, 255, 0.18)',
    backdropFilter: 'blur(12px)',
    borderRadius: '12px',
    marginTop: '8px',
    minWidth: '180px'
  }
};

function NavBar() {
  const [anchorElNav, setAnchorElNav] = React.useState(null);
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isLoggedIn = Boolean(localStorage.getItem('token'));

  let userDetails = {};
  try {
    userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
  } catch {
    userDetails = {};
  }

  const displayName = userDetails?.name || userDetails?.fullName || userDetails?.username || userDetails?.email || 'User';
  const avatarUrl = userDetails?.photo || userDetails?.photoUrl || userDetails?.avatar || userDetails?.profileImage;

  const initials = React.useMemo(() => {
    const parts = String(displayName).trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return String(displayName).slice(0, 2).toUpperCase();
  }, [displayName]);

  const handleCloseNavMenu = (route) => {
    setAnchorElNav(null);
    if (typeof route === 'string') {
      navigate(route);
    }
  };

  const handleSetting = (route) => {
    setAnchorElUser(null);
    if (route === 'logout') {
      localStorage.clear();
      navigate('/login');
      return;
    }
    navigate(route);
  };

  const hiddenRoutes = new Set(['/login', '/register']);
  if (hiddenRoutes.has(location.pathname)) {
    return null;
  }

  return (
    <AppBar position="sticky" elevation={0} className="cp-appbar">
      <Container maxWidth="xl">
        <Toolbar disableGutters className="cp-toolbar">
          <Box sx={{ display: { xs: 'flex', md: 'none' }, mr: 1 }}>
            <IconButton
              size="large"
              aria-label="navigation menu"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={(event) => setAnchorElNav(event.currentTarget)}
              className="cp-menu-btn"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              keepMounted
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              open={Boolean(anchorElNav)}
              onClose={() => handleCloseNavMenu()}
              sx={{ display: { xs: 'block', md: 'none' }, ...menuPaperSx }}
            >
              {pages.map((page) => (
                <MenuItem key={page.name} onClick={() => handleCloseNavMenu(page.navigate)}>
                  <Typography textAlign="center">{page.name}</Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>

          <button className="cp-brand" onClick={() => navigate('/')} type="button" aria-label="Go to home page">
            <img src={logo} alt="Cricket Prediction" className="cp-brand-logo" />
            <span className="cp-brand-text">Cricket Prediction</span>
          </button>

          <Box className="cp-nav-links" sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
            {pages.map((page) => {
              const isActive = location.pathname === page.navigate || location.pathname.startsWith(`${page.navigate}/`);
              return (
                <Button
                  key={page.name}
                  onClick={() => handleCloseNavMenu(page.navigate)}
                  className={`cp-nav-btn ${isActive ? 'is-active' : ''}`}
                >
                  {page.name}
                </Button>
              );
            })}
          </Box>

          <Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isLoggedIn ? (
              <>
                <Tooltip title="Open settings">
                  <IconButton onClick={(event) => setAnchorElUser(event.currentTarget)} sx={{ p: 0 }}>
                    <Avatar
                      alt={displayName}
                      src={avatarUrl || undefined}
                      sx={!avatarUrl ? { bgcolor: '#163740', color: '#f0f9f6', fontWeight: 700 } : { border: '2px solid rgba(255, 255, 255, 0.45)' }}
                    >
                      {!avatarUrl ? initials : null}
                    </Avatar>
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={anchorElUser}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  keepMounted
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  open={Boolean(anchorElUser)}
                  onClose={() => setAnchorElUser(null)}
                  sx={menuPaperSx}
                >
                  {settings.map((setting) => (
                    <MenuItem key={setting.name} onClick={() => handleSetting(setting.navigate)}>
                      <Typography textAlign="center">{setting.name}</Typography>
                    </MenuItem>
                  ))}
                </Menu>
              </>
            ) : (
              <>
                <Button className="cp-auth-btn cp-auth-btn--ghost" onClick={() => navigate('/login')}>Login</Button>
                <Button className="cp-auth-btn cp-auth-btn--solid" onClick={() => navigate('/register')}>Register</Button>
              </>
            )}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

export default NavBar;
